import { createContext, useContext, useState, useEffect } from "react";

/**
 * Context for managing user authentication state across the application
 */
const UserContext = createContext();

/**
 * Provider component that manages user authentication state
 * Handles login, logout, and persistent user sessions
 */
export const UserProvider = ({ children }) => {
  const [user, setUser] = useState(null);

  /**
   * Effect to check for existing authentication token on app initialization
   * Automatically logs in user if valid token exists in localStorage
   */
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) {
      // Fetch user data using the stored token
      fetch(process.env.REACT_APP_API_URL + "/me", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
        .then((res) => {
          if (!res.ok) {
            throw new Error("Failed to fetch user data");
          }
          return res.json();
        })
        .then((data) => {
          if (data.email) {
            // Store complete user data including username
            setUser(data);
          }
        })
        .catch((error) => {
          console.error("Error fetching user data:", error);
          // Keep token for retry - removed automatic removal
          setUser(null);
        });
    }
  }, []);

  /**
   * Handles user login by storing token and fetching user data
   * @param {string} token - JWT authentication token
   */
  const login = (token) => {
    localStorage.setItem("token", token);
    
    // Fetch user data after successful login
    fetch(process.env.REACT_APP_API_URL + "/me", {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })
      .then((res) => {
        if (!res.ok) {
          throw new Error("Failed to fetch user data");
        }
        return res.json();
      })
      .then((data) => {
        setUser(data);
      })
      .catch((error) => {
        console.error("Error during login:", error);
        // Keep token for retry - removed automatic removal
        setUser(null);
      });
  };

  /**
   * Handles user logout by clearing token and user data
   */
  const logout = () => {
    localStorage.removeItem("token");
    setUser(null);
    console.log("User logged out");
  };

  return (
    <UserContext.Provider value={{ user, setUser, login, logout }}>
      {children}
    </UserContext.Provider>
  );
};

/**
 * Custom hook to access user context
 * @returns {Object} User context containing user data and auth methods
 */
export const useUser = () => useContext(UserContext);